/**
 * BeezFy Premium ATC System v3.0
 * Complete unified system with all admin settings
 */

(function() {
  'use strict';

  class BeezfyPremiumATC {
    constructor() {
      this.settings = window.BeezfyTheme?.settings || {};
      this.cart = null;
      this.isProcessing = false;
      this.activePopup = null;
      this.activeToasts = [];

      // Complete configuration from admin settings
      this.config = {
        // Main behavior
        action: this.settings.atc_action_type || 'popup',
        combineActions: this.settings.atc_combine_actions === true,
        ajaxAdd: this.settings.atc_ajax_add !== false,
        preventDuplicate: this.settings.atc_prevent_duplicate === true,

        // Premium popup settings
        popup: {
          enabled: this.settings.atc_popup_enabled !== false,
          animation: this.settings.atc_popup_animation || 'fade-scale',
          delay: parseInt(this.settings.atc_popup_delay) || 100,
          autoClose: parseInt(this.settings.atc_popup_auto_close) || 0,
          position: this.settings.atc_popup_position || 'center',
          colorScheme: this.settings.atc_popup_color_scheme || 'scheme-1'
        },

        // Marketing features
        marketing: {
          showRecommendations: this.settings.atc_show_recommendations !== false,
          recommendationSource: this.settings.atc_recommendation_source || 'related',
          recommendationCount: parseInt(this.settings.atc_recommendation_count) || 4,
          showUpsell: this.settings.atc_show_upsell !== false,
          showBundleOffer: this.settings.atc_show_bundle_offer !== false,
          showCartSummary: this.settings.atc_show_cart_summary !== false,
          showFreeShippingBar: this.settings.atc_show_free_shipping_bar !== false,
          showSavings: this.settings.atc_show_savings !== false,
          showRewardPoints: this.settings.atc_show_reward_points === true,
          freeShippingThreshold: parseFloat(this.settings.free_shipping_threshold) || 100
        },

        // Toast settings
        toast: {
          enabled: this.settings.atc_toast_enabled !== false,
          position: this.settings.atc_toast_position || 'bottom-right',
          duration: parseInt(this.settings.atc_toast_duration) * 1000 || 4000,
          stack: this.settings.atc_toast_stack !== false
        },

        // Visual features
        visual: {
          showProductImage: this.settings.atc_show_product_image !== false,
          showTrustBadges: this.settings.atc_show_trust_badges !== false,
          showPaymentIcons: this.settings.atc_show_payment_icons !== false
        },

        // Advanced features
        advanced: {
          vibrateMobile: this.settings.atc_vibrate_mobile !== false,
          soundEffect: this.settings.atc_sound_effect === true,
          confettiEffect: this.settings.atc_confetti_effect === true
        },

        // Mobile optimization
        mobile: {
          behavior: this.settings.atc_mobile_behavior || 'drawer',
          stickyButton: this.settings.atc_sticky_add_button !== false
        },

        // Variant wizard settings (default: auto for fashion best practice)
        variantWizard: {
          mode: this.settings.cart_variant_wizard || 'auto', // auto = only for products with variants
          singleVariant: this.settings.cart_wizard_single_variant || false, // don't show for single variant
          closeOnAdd: this.settings.cart_wizard_close_on_add !== false,
          productPageEnabled: this.settings.product_variant_wizard_enabled !== false, // enabled by default
          productPageTrigger: this.settings.product_wizard_trigger || 'incomplete' // open when selection incomplete
        },

        // Texts (internationalization with translations)
        texts: this.loadTranslations()
      };

      // Mobile detection - but keep same behavior as desktop
      this.isMobile = window.matchMedia('(max-width: 768px)').matches;
      // IMPORTANT: Always use same behavior on mobile as desktop per admin settings
      // Don't override with mobile.behavior - it should follow main atc_action_type
      // Only use mobile.behavior if explicitly set to something other than 'same'
      if (this.isMobile && this.config.mobile.behavior !== 'same' && this.config.mobile.behavior !== 'drawer') {
        // Only override if explicitly set to popup/toast/notification
        if (this.config.mobile.behavior === 'popup' || this.config.mobile.behavior === 'toast') {
          this.config.action = this.config.mobile.behavior;
        }
      }

      this.init();
    }

    loadTranslations() {
      const t = window.beezfyTranslations || {};
      return {
        success: this.settings.atc_success_message || t.addedToCart || 'Aggiunto al carrello',
        continueShopping: this.settings.atc_continue_shopping_text || t.continueShopping || 'Continua lo shopping',
        viewCart: this.settings.atc_view_cart_text || t.viewCart || 'Vai al carrello',
        checkout: this.settings.atc_checkout_text || t.checkout || 'Vai alla cassa',
        freeShippingSuccess: this.settings.free_shipping_success_message || t.freeShippingSuccess || "Hai la spedizione gratuita!",
        addToCart: t.addToCart || 'Aggiungi al carrello',
        soldOut: t.soldOut || 'Sold out',
        unavailable: t.unavailable || 'Non disponibile',
        selectOptions: t.selectOptions || 'Scegli le opzioni',
        youMightAlsoLike: t.youMightAlsoLike || 'Potrebbe piacerti anche',
        itemsInCart: t.itemsInCart || '{count} articoli nel carrello',
        youSaved: t.youSaved || 'Hai risparmiato {amount}!',
        freeShipping: t.freeShipping || '{amount} alla spedizione gratuita',
        secureCheckout: t.secureCheckout || 'Pagamento sicuro',
        fastShipping: t.fastShipping || 'Spedizione rapida',
        dayReturns: t.dayReturns || 'Reso in 30 giorni',
        outOfStock: t.outOfStock || 'Sold out',
        productNotAvailable: t.productNotAvailable || 'Prodotto non disponibile',
        loading: t.loading || 'Caricamento...',
        error: t.error || 'Si è verificato un errore',
        quantity: t.quantity || 'Qtà'
      };
    }

    init() {
      // Idempotent: attach delegated document listeners / load assets ONCE.
      // reinit() (bzf-filters load-more) may call init() on every page load;
      // without this guard each call would stack duplicate document-level
      // submit/click listeners and recreate the Audio object, leaking memory
      // until the mobile webview renderer crashes ("Si e' verificato un errore").
      // New cards are handled automatically via event delegation, so re-running
      // attachEventListeners on load-more is both unnecessary and harmful.
      if (this._initialized) {
        // Make sure the global handle stays valid, then bail.
        window.BeezfyATCSystem = this;
        return;
      }
      this._initialized = true;

      this.attachEventListeners();
      this.initializePopup();
      this.loadAssets();

      // Make globally accessible
      window.BeezfyATCSystem = this;
    }

    loadAssets() {
      // Preload success sound if enabled
      if (this.config.advanced.soundEffect) {
        this.successSound = new Audio(this.settings.cart_sound_file || '/assets/cart-add-sound.mp3');
        this.successSound.volume = 0.3;
      }

      // Load confetti library if enabled
      if (this.config.advanced.confettiEffect && !window.confetti) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.5.1/dist/confetti.browser.min.js';
        document.head.appendChild(script);
      }
    }

    shouldOpenVariantWizard(button) {
      // Check if we're on a product page with variants
      const isProductPage = window.beezfyProductPremium || window.beezfyProductPage;
      if (!isProductPage) return false;

      // Get the active product system
      const productSystem = window.beezfyProductPremium || window.beezfyProductPage;

      // Check if product has multiple variants
      const hasMultipleVariants = productSystem.product?.variants?.length > 1;
      if (!hasMultipleVariants) return false;

      // Check admin settings
      const alwaysShowWizard = window.beezfySettings?.always_show_variant_wizard === true ||
                               window.BeezfyTheme?.settings?.always_show_variant_wizard === true;

      if (alwaysShowWizard) {
        return true;
      }

      // Otherwise check if selection is missing
      const currentVariant = productSystem.currentVariant;
      const hasMissingOptions = productSystem.hasMissingOptions ? productSystem.hasMissingOptions() : false;
      const needsVariantSelection = !currentVariant || hasMissingOptions;


      return needsVariantSelection;
    }

    attachEventListeners() {
      // Single event listener for form submits (capture phase)
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.action && form.action.includes('/cart/add')) {
          if (this.config.ajaxAdd) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.handleFormSubmit(form);
          }
        }
      }, true);

      // Single event listener for button clicks (capture phase)
      document.addEventListener('click', (e) => {
        const button = e.target.closest('[data-add-to-cart], [data-variant-wizard-trigger], .bz-card__atc-btn');

        if (button) {
          // Skip if product page is handling this button
          if (button.hasAttribute('data-skip-atc-system')) {
            return;
          }

          if (button.hasAttribute('data-variant-wizard-trigger')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.openVariantWizard(button);
          } else if (button.hasAttribute('data-add-to-cart')) {

            // Check if variant wizard should open instead
            const shouldOpenWizard = this.shouldOpenVariantWizard(button);

            if (shouldOpenWizard) {
              // Selezione incompleta su prodotto variabile → apri il Variant Wizard.
              // (Necessario soprattutto dopo lo swap dello scrubber, dove il product page
              //  non ri-aggancia un handler dedicato.) openVariantWizard usa data-product-handle
              //  e fetcha il prodotto fresco, quindi è corretto anche dopo lo scrub.
              if (button.dataset.productHandle && window.variantWizard) {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.openVariantWizard(button);
              }
              return;
            }

            const form = button.closest('form');
            if (form && this.config.ajaxAdd) {
              e.preventDefault();
              e.stopImmediatePropagation();
              this.handleFormSubmit(form);
            }
          }
        }
      }, true);
    }

    async handleFormSubmit(form) {
      if (this.isProcessing) return;

      this.isProcessing = true;
      const button = form.querySelector ?
        form.querySelector('[type="submit"], [data-add-to-cart]') :
        null;

      if (button) {
        button.classList.add('loading');
        button.disabled = true;
      }

      try {
        // Handle both real forms and virtual forms
        let formData;
        if (form instanceof HTMLFormElement) {
          formData = new FormData(form);
        } else if (form.body) {
          // Virtual form from variant wizard
          formData = form.body;
        } else {
          // Create FormData from properties
          formData = new FormData();
          if (form.id) formData.append('id', form.id);
          if (form.quantity) formData.append('quantity', form.quantity);
          if (form.properties) {
            Object.entries(form.properties).forEach(([key, value]) => {
              formData.append(`properties[${key}]`, value);
            });
          }
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add to cart');
        }

        const item = await response.json();

        // Update cart
        await this.updateCart();

        // Handle success with all features
        await this.handleSuccess(item);

        // Dispatch event
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item, form }
        }));

      } catch (error) {
        console.error('[BeezFy Premium ATC] Error:', error);
        this.showError(error.message);
      } finally {
        if (button) {
          button.classList.remove('loading');
          button.disabled = false;
        }
        this.isProcessing = false;
      }
    }

    async updateCart() {
      try {
        const response = await fetch('/cart.js');

        // Handle rate limiting
        if (response.status === 429) {
          console.warn('[BeezFy Premium ATC] Rate limited, retrying in 2 seconds...');
          setTimeout(() => this.updateCart(), 2000);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch cart: ${response.status}`);
        }

        this.cart = await response.json();

        this.updateCartUI();

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: this.cart }
        }));
      } catch (error) {
        console.error('[BeezFy Premium ATC] Failed to update cart:', error);
      }
    }

    updateCartUI() {
      // Update cart count
      const cartCounts = document.querySelectorAll('[data-cart-count]');
      cartCounts.forEach(el => {
        el.textContent = this.cart.item_count;
      });

      // Update cart icon bubble
      const cartBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (cartBubble) {
        cartBubble.textContent = this.cart.item_count;
        cartBubble.classList.toggle('hidden', this.cart.item_count === 0);
      }

      // Shake cart icon if enabled
      if (this.settings.cart_shake_icon) {
        const cartIcon = document.querySelector('.header__icon--cart');
        if (cartIcon) {
          cartIcon.classList.add('shake');
          setTimeout(() => cartIcon.classList.remove('shake'), 600);
        }
      }
    }

    async handleSuccess(item) {
      // Haptic feedback
      if (this.isMobile && this.config.advanced.vibrateMobile && navigator.vibrate) {
        navigator.vibrate(50);
      }

      // Play success sound
      if (this.config.advanced.soundEffect && this.successSound) {
        this.successSound.play().catch(() => {});
      }

      // Show confetti
      if (this.config.advanced.confettiEffect && window.confetti) {
        this.showConfetti();
      }

      // Handle primary action
      switch (this.config.action) {
        case 'popup':
          await this.showPremiumPopup(item);
          break;

        case 'drawer':
          this.openCartDrawer();
          break;

        case 'notification':
        case 'toast':
          this.showToast(item);
          break;

        case 'page':
          window.location.href = '/cart';
          break;

        case 'none':
          // Silent mode
          break;
      }

      // Handle combined actions
      if (this.config.combineActions) {
        if (this.config.action !== 'notification' && this.config.toast.enabled) {
          this.showToast(item);
        }
      }
    }

    initializePopup() {
      if (!document.getElementById('beezfy-atc-popup')) {
        const popup = document.createElement('div');
        popup.id = 'beezfy-atc-popup';
        popup.className = `beezfy-atc-popup beezfy-atc-popup--${this.config.popup.position}`;
        popup.innerHTML = this.getPopupTemplate();
        document.body.appendChild(popup);

        this.bindPopupEvents();
      }
    }

    getPopupTemplate() {
      return `
        <div class="beezfy-atc-popup__overlay"></div>
        <div class="beezfy-atc-popup__container ${this.config.popup.colorScheme}">
          <button class="beezfy-atc-popup__close" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>

          <div class="beezfy-atc-popup__content">
            <!-- Success Header -->
            <div class="beezfy-atc-popup__header">
              <svg class="beezfy-atc-popup__success-icon" width="40" height="40" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="23" fill="none" stroke="#1a1a1a" stroke-width="1.4"/>
                <path d="M16 24.5l5.5 5.5L33 18" stroke="#1a1a1a" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <h3 class="beezfy-atc-popup__title">${this.config.texts.success}</h3>
            </div>

            <!-- Product Info -->
            <div class="beezfy-atc-popup__product ${!this.config.visual.showProductImage ? 'no-image' : ''}">
              ${this.config.visual.showProductImage ? `
                <img class="beezfy-atc-popup__image" src="" alt="">
              ` : ''}
              <div class="beezfy-atc-popup__details">
                <h4 class="beezfy-atc-popup__product-title"></h4>
                <div class="beezfy-atc-popup__variant"></div>
                <div class="beezfy-atc-popup__price-quantity">
                  <span class="beezfy-atc-popup__price"></span>
                  <span class="beezfy-atc-popup__quantity"></span>
                </div>
              </div>
            </div>

            <!-- Free Shipping Bar -->
            ${this.config.marketing.showFreeShippingBar ? `
              <div class="beezfy-atc-popup__shipping-bar">
                <div class="beezfy-atc-popup__shipping-progress">
                  <div class="beezfy-atc-popup__shipping-fill"></div>
                </div>
                <p class="beezfy-atc-popup__shipping-text"></p>
              </div>
            ` : ''}

            <!-- Cart Summary -->
            ${this.config.marketing.showCartSummary ? `
              <div class="beezfy-atc-popup__cart-summary">
                <div class="beezfy-atc-popup__cart-info">
                  <span class="beezfy-atc-popup__cart-items"></span>
                  <span class="beezfy-atc-popup__cart-total"></span>
                </div>
                ${this.config.marketing.showSavings ? `
                  <div class="beezfy-atc-popup__savings"></div>
                ` : ''}
              </div>
            ` : ''}

            <!-- Recommendations -->
            ${this.config.marketing.showRecommendations ? `
              <div class="beezfy-atc-popup__recommendations">
                <h4 class="beezfy-atc-popup__recommendations-title">${this.config.texts.youMightAlsoLike}</h4>
                <div class="beezfy-atc-popup__recommendations-grid"></div>
              </div>
            ` : ''}

            <!-- Trust Badges -->
            ${this.config.visual.showTrustBadges ? `
              <div class="beezfy-atc-popup__trust">
                <div class="beezfy-atc-popup__trust-badge">
                  <svg width="16" height="16" fill="currentColor">
                    <path d="M8 0L6.5 5H1l4.5 3.5L4 14l4-3 4 3-1.5-5.5L15 5H9.5L8 0z"/>
                  </svg>
                  <span>${this.config.texts.secureCheckout}</span>
                </div>
                <div class="beezfy-atc-popup__trust-badge">
                  <svg width="16" height="16" fill="currentColor">
                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm3.5 6L7 10.5 4.5 8l1-1L7 8.5l3.5-3.5 1 1z"/>
                  </svg>
                  <span>${this.config.texts.dayReturns}</span>
                </div>
                <div class="beezfy-atc-popup__trust-badge">
                  <svg width="16" height="16" fill="currentColor">
                    <path d="M8 2C4.7 2 2 4.7 2 8s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm2.5 4.5L7 10 5.5 8.5l1-1L7 8l2.5-2.5 1 1z"/>
                  </svg>
                  <span>${this.config.texts.fastShipping}</span>
                </div>
              </div>
            ` : ''}

            <!-- Actions -->
            <div class="beezfy-atc-popup__actions">
              <button class="beezfy-atc-popup__continue" data-action="continue">
                ${this.config.texts.continueShopping}
              </button>
              <a href="/cart" class="beezfy-atc-popup__view-cart">
                ${this.config.texts.viewCart}
              </a>
              <a href="/checkout" class="beezfy-atc-popup__checkout">
                ${this.config.texts.checkout}
              </a>
            </div>

            <!-- Payment Icons -->
            ${this.config.visual.showPaymentIcons ? `
              <div class="beezfy-atc-popup__payment-icons">
                ${this.getPaymentIcons()}
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }

    getPaymentIcons() {
      // Use Shopify's payment icons or fallback
      const paymentMethods = ['visa', 'mastercard', 'amex', 'paypal', 'apple-pay', 'google-pay', 'shop-pay'];
      const icons = paymentMethods.map(method => {
        return `<svg class="payment-icon payment-icon--${method}" xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 38 24" width="38" height="24" aria-labelledby="pi-${method}">
          <title id="pi-${method}">${method}</title>
          <use href="#icon-payment-${method}"></use>
        </svg>`;
      });

      // Fallback to generic payment text if icons not available
      return icons.join('') || '<span style="font-size: 12px; opacity: 0.7;">Secure Payment</span>';
    }

    bindPopupEvents() {
      const popup = document.getElementById('beezfy-atc-popup');
      if (!popup) return;

      const closeBtn = popup.querySelector('.beezfy-atc-popup__close');
      const overlay = popup.querySelector('.beezfy-atc-popup__overlay');
      const continueBtn = popup.querySelector('[data-action="continue"]');

      if (closeBtn) closeBtn.addEventListener('click', () => this.closePopup());
      if (overlay) overlay.addEventListener('click', () => this.closePopup());
      if (continueBtn) continueBtn.addEventListener('click', () => this.closePopup());
    }

    async showPremiumPopup(item) {
      const popup = document.getElementById('beezfy-atc-popup');
      if (!popup) return;

      // Reset scroll to top BEFORE showing
      const popupContent = popup.querySelector('.beezfy-atc-popup__content');
      if (popupContent) {
        popupContent.scrollTop = 0;
      }

      // Wait for delay
      if (this.config.popup.delay > 0) {
        await new Promise(resolve => setTimeout(resolve, this.config.popup.delay));
      }

      // Update popup content
      this.updatePopupContent(popup, item);

      // Load recommendations if enabled
      if (this.config.marketing.showRecommendations) {
        this.loadRecommendations(item);
      }

      // Update free shipping bar
      if (this.config.marketing.showFreeShippingBar) {
        this.updateFreeShippingBar();
      }

      // Add animation class
      popup.classList.add(`animation-${this.config.popup.animation}`);

      // Show popup
      popup.classList.add('active');
      document.body.classList.add('popup-open');

      // Ensure scroll is at top after showing (for safety)
      requestAnimationFrame(() => {
        if (popupContent) {
          popupContent.scrollTop = 0;
        }
      });

      // Auto close if configured
      if (this.config.popup.autoClose > 0) {
        setTimeout(() => this.closePopup(), this.config.popup.autoClose * 1000);
      }
    }

    updatePopupContent(popup, item) {
      // Update product image
      if (this.config.visual.showProductImage) {
        const image = popup.querySelector('.beezfy-atc-popup__image');
        if (image && item.image) {
          image.src = item.image;
        }
      }

      // Update product details
      const title = popup.querySelector('.beezfy-atc-popup__product-title');
      if (title) title.textContent = item.product_title || item.title;

      const variant = popup.querySelector('.beezfy-atc-popup__variant');
      if (variant && item.variant_title && item.variant_title !== 'Default Title') {
        variant.textContent = item.variant_title;
        variant.style.display = 'block';
      } else if (variant) {
        variant.style.display = 'none';
      }

      const price = popup.querySelector('.beezfy-atc-popup__price');
      if (price) price.textContent = this.formatMoney(item.price);

      const quantity = popup.querySelector('.beezfy-atc-popup__quantity');
      if (quantity) quantity.textContent = `${this.config.texts.quantity}: ${item.quantity}`;

      // Update cart summary
      if (this.config.marketing.showCartSummary) {
        const cartItems = popup.querySelector('.beezfy-atc-popup__cart-items');
        if (cartItems) { const n = this.cart?.item_count || 0; let txt = this.config.texts.itemsInCart.replace('{count}', n); if (n === 1) txt = txt.replace('articoli', 'articolo'); cartItems.textContent = txt; }

        const cartTotal = popup.querySelector('.beezfy-atc-popup__cart-total');
        if (cartTotal) cartTotal.textContent = this.formatMoney(this.cart?.total_price || 0);

        if (this.config.marketing.showSavings && this.cart?.total_discount > 0) {
          const savings = popup.querySelector('.beezfy-atc-popup__savings');
          if (savings) {
            savings.textContent = this.config.texts.youSaved.replace('{amount}', this.formatMoney(this.cart.total_discount));
            savings.style.display = 'block';
          }
        }
      }

      // Update view cart button
      const viewCart = popup.querySelector('.beezfy-atc-popup__view-cart');
      if (viewCart) {
        viewCart.textContent = `${this.config.texts.viewCart} (${this.cart?.item_count || 0})`;
      }
    }

    updateFreeShippingBar() {
      const threshold = this.config.marketing.freeShippingThreshold * 100; // Convert to cents
      const currentTotal = this.cart?.total_price || 0;
      const remaining = threshold - currentTotal;

      const fill = document.querySelector('.beezfy-atc-popup__shipping-fill');
      const text = document.querySelector('.beezfy-atc-popup__shipping-text');

      if (fill && text) {
        const percentage = Math.min(100, (currentTotal / threshold) * 100);
        fill.style.width = `${percentage}%`;

        if (remaining > 0) {
          text.textContent = this.config.texts.freeShipping.replace('{amount}', this.formatMoney(remaining));
        } else {
          text.textContent = this.config.texts.freeShippingSuccess;
          text.classList.add('success');
        }
      }
    }

    async loadRecommendations(item) {
      const grid = document.querySelector('.beezfy-atc-popup__recommendations-grid');
      if (!grid) return;

      try {
        // Load recommendations based on source
        let products = [];

        if (this.config.marketing.recommendationSource === 'related') {
          // Load related products
          const response = await fetch(`/recommendations/products.json?product_id=${item.product_id}&limit=${this.config.marketing.recommendationCount}`);
          const data = await response.json();
          products = data.products || [];
        }

        // Render recommendations
        if (products.length > 0) {
          grid.innerHTML = products.map(product => `
            <a href="${product.url}" class="beezfy-atc-popup__recommendation">
              <img src="${product.featured_image}" alt="${product.title}">
              <h5>${product.title}</h5>
              <span class="price">${this.formatMoney(product.price)}</span>
            </a>
          `).join('');
        }
      } catch (error) {
        console.error('[BeezFy Premium ATC] Failed to load recommendations:', error);
      }
    }

    closePopup() {
      const popup = document.getElementById('beezfy-atc-popup');
      if (popup) {
        popup.classList.remove('active');
        document.body.classList.remove('popup-open');
      }
    }

    showToast(item) {
      const toast = document.createElement('div');
      toast.className = `beezfy-toast beezfy-toast--${this.config.toast.position} ${this.config.popup.colorScheme}`;
      toast.innerHTML = `
        <div class="beezfy-toast__content">
          <svg class="beezfy-toast__icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm-2 15l-5-5 1.41-1.41L8 12.17l7.59-7.59L17 6l-9 9z" fill="#10B981"/>
          </svg>
          <span class="beezfy-toast__message">
            ${item.product_title || 'Item'} added to cart
          </span>
          <button class="beezfy-toast__close" aria-label="Close">×</button>
        </div>
      `;

      document.body.appendChild(toast);

      // Animate in
      requestAnimationFrame(() => {
        toast.classList.add('active');
      });

      // Close button
      toast.querySelector('.beezfy-toast__close').addEventListener('click', () => {
        toast.classList.remove('active');
        setTimeout(() => toast.remove(), 300);
      });

      // Auto close
      setTimeout(() => {
        toast.classList.remove('active');
        setTimeout(() => toast.remove(), 300);
      }, this.config.toast.duration);

      // Stack management
      if (this.config.toast.stack) {
        this.activeToasts.push(toast);
        this.updateToastPositions();
      }
    }

    updateToastPositions() {
      let offset = 20;
      this.activeToasts.forEach(toast => {
        if (toast && toast.parentElement) {
          toast.style.bottom = `${offset}px`;
          offset += toast.offsetHeight + 10;
        }
      });

      // Clean up removed toasts
      this.activeToasts = this.activeToasts.filter(t => t.parentElement);
    }

    openCartDrawer() {
      // Use unified drawer system if available
      if (window.beezfyDrawer && typeof window.beezfyDrawer.open === 'function') {
        window.beezfyDrawer.open('cart');
        return;
      }

      // Try to find and open cart drawer
      const drawer = document.querySelector('#CartDrawer, #cart-drawer, .cart-drawer, [data-cart-drawer]');
      if (drawer) {
        drawer.classList.add('active', 'is-open');
        drawer.removeAttribute('hidden');
        document.body.classList.add('cart-drawer-open', 'overflow-hidden');

        document.dispatchEvent(new CustomEvent('drawer:open', {
          detail: { type: 'cart' }
        }));
      }
    }

    async openVariantWizard(button) {
      const productHandle = button.dataset.productHandle;

      if (!productHandle) {
        console.error('[BeezFy Premium ATC] No product handle found');
        return;
      }

      try {
        const response = await fetch(`/products/${productHandle}.js`);
        const product = await response.json();

        // Check if product has real variants (not just Default Title)
        const hasRealVariants = product.options && product.options.length > 0 &&
          !(product.options.length === 1 && product.options[0].values.length === 1 &&
            product.options[0].values[0] === 'Default Title');

        // Check variant wizard settings
        if (this.config.variantWizard.mode === 'never') {
          // Direct add to cart with first available variant
          if (product.variants && product.variants.length > 0) {
            const availableVariant = product.variants.find(v => v.available) || product.variants[0];
            await this.addToCart(availableVariant.id, 1, { button });
          }
          return;
        }

        // Determine if wizard should open
        const shouldOpenWizard = hasRealVariants && (
          this.config.variantWizard.mode === 'always' ||
          (this.config.variantWizard.mode === 'auto' && product.options.length > 0)
        );

        if (shouldOpenWizard && window.variantWizard) {
          // Open wizard for products with real variants
          window.variantWizard.open(product);
        } else if (!hasRealVariants && product.variants && product.variants.length > 0) {
          // Direct add for simple products (no real variants)
          const availableVariant = product.variants.find(v => v.available);
          if (availableVariant) {
            await this.addToCart(availableVariant.id, 1, { button });
          } else {
            this.showError('Product is out of stock');
          }
        } else if (!product.variants || product.variants.length === 0) {
          // No variants at all - shouldn't happen but handle gracefully
          this.showError('Product not available');
        } else {
          // Fallback to product page if needed
          window.location.href = `/products/${productHandle}`;
        }

      } catch (error) {
        console.error('[BeezFy Premium ATC] Failed to load product:', error);
        this.showError('Failed to load product information');
      }
    }

    showConfetti() {
      if (window.confetti) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#10B981', '#3B82F6', '#F59E0B']
        });
      }
    }

    showError(message) {
      const error = document.createElement('div');
      error.className = 'beezfy-error-notification';
      error.textContent = message || 'An error occurred. Please try again.';

      document.body.appendChild(error);

      requestAnimationFrame(() => {
        error.classList.add('active');
      });

      setTimeout(() => {
        error.classList.remove('active');
        setTimeout(() => error.remove(), 300);
      }, 5000);
    }

    formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || window.Shopify.money_format);
      return `€${(cents / 100).toFixed(2).replace('.', ',')}`; // mai dollari
    }

    // Public API
    async addToCart(variantId, quantity = 1, options = {}) {
      // Create virtual form object for handleFormSubmit
      const virtualForm = {
        id: variantId,
        quantity: quantity,
        properties: options.properties || {}
      };

      // Add button reference if provided
      if (options.button) {
        virtualForm.querySelector = () => options.button;
      }

      return this.handleFormSubmit(virtualForm);
    }
  }

  // Initialize when DOM is ready and expose globally
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.BeezfyATCSystem = new BeezfyPremiumATC();
    });
  } else {
    window.BeezfyATCSystem = new BeezfyPremiumATC();
  }

})();